import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";

import { HeaderTitle } from "../common/titles";
import { LoaderDots } from "../../../middleware/loaders";
import { friendlyError } from "../../../middleware/common-functions";
import PlanCards from "./planCards";
import {
  GET_SUBSCRIPTION_PLANS,
  GET_MY_SUBSCRIPTION,
  GET_PLAN_USAGE,
  CREATE_SUBSCRIPTION_CHECKOUT,
  UPGRADE_SUBSCRIPTION,
  CANCEL_SUBSCRIPTION,
  REACTIVATE_SUBSCRIPTION,
  MANAGE_BILLING,
  GetSubscriptionPlansResponse,
  GetMySubscriptionResponse,
  GetPlanUsageResponse,
  CreateSubscriptionCheckoutResponse,
  CreateSubscriptionCheckoutVariables,
  UpgradeSubscriptionResponse,
  UpgradeSubscriptionVariables,
  CancelSubscriptionResponse,
  ReactivateSubscriptionResponse,
  ManageBillingResponse,
} from "./queries";

const planTypeLabelsModal: Record<string, string> = {
  basic: "Basico",
  premium: "Premium",
  pro: "Pro",
};

const planColorsModal: Record<string, string> = {
  basic: "#48BB78",
  premium: "#FFBB28",
  pro: "#FF6028",
};

const planFeaturesModal: Record<string, string[]> = {
  basic: ["1 sucursal", "2 usuarios", "500 ordenes/mes"],
  premium: ["2 sucursales", "20 usuarios", "1,500 ordenes/mes"],
  pro: ["4 sucursales", "Usuarios ilimitados", "5,000 ordenes/mes"],
};

const SubscriptionPage: React.FC = () => {
  const location = useLocation();
  const [upgradeModal, setUpgradeModal] = useState<{
    open: boolean;
    planType: string;
    billingInterval: string;
  }>({ open: false, planType: "", billingInterval: "yearly" });

  const { data: plansData, loading: plansLoading } =
    useQuery<GetSubscriptionPlansResponse>(GET_SUBSCRIPTION_PLANS, {
      fetchPolicy: "cache-and-network",
    });

  const {
    data: subData,
    loading: subLoading,
    refetch: refetchSub,
  } = useQuery<GetMySubscriptionResponse>(GET_MY_SUBSCRIPTION, {
    fetchPolicy: "cache-and-network",
  });

  const {
    data: usageData,
    loading: usageLoading,
    refetch: refetchUsage,
  } = useQuery<GetPlanUsageResponse>(GET_PLAN_USAGE, {
    fetchPolicy: "cache-and-network",
  });

  const [createCheckout, { loading: checkoutLoading }] = useMutation<
    CreateSubscriptionCheckoutResponse,
    CreateSubscriptionCheckoutVariables
  >(CREATE_SUBSCRIPTION_CHECKOUT);

  const [upgradeSubscription, { loading: upgradeLoading }] = useMutation<
    UpgradeSubscriptionResponse,
    UpgradeSubscriptionVariables
  >(UPGRADE_SUBSCRIPTION);

  const [cancelSubscription, { loading: cancelLoading }] =
    useMutation<CancelSubscriptionResponse>(CANCEL_SUBSCRIPTION);

  const [reactivateSubscription, { loading: reactivateLoading }] =
    useMutation<ReactivateSubscriptionResponse>(REACTIVATE_SUBSCRIPTION);

  const [manageBilling, { loading: billingLoading }] =
    useMutation<ManageBillingResponse>(MANAGE_BILLING);

  // Handle return from Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    if (status === "success") {
      toast.success("Suscripción activada correctamente");
      refetchSub();
      refetchUsage();
    } else if (status === "cancelled") {
      toast.info("Checkout cancelado");
    }
  }, [location.search, refetchSub, refetchUsage]);

  const handleSubscribe = async (planType: string, billingInterval: string = "yearly") => {
    try {
      const { data } = await createCheckout({
        variables: { planType, billingInterval },
      });
      if (data?.createSubscriptionCheckout.url) {
        window.location.href = data.createSubscriptionCheckout.url;
      }
    } catch (e: any) {
      toast.error(friendlyError(e));
    }
  };

  const handleUpgrade = (newPlanType: string, billingInterval: string = "yearly") => {
    setUpgradeModal({ open: true, planType: newPlanType, billingInterval });
  };

  const confirmUpgrade = async () => {
    const { planType, billingInterval } = upgradeModal;
    try {
      await upgradeSubscription({
        variables: { newPlanType: planType, billingInterval },
      });
      setUpgradeModal({ open: false, planType: "", billingInterval: "yearly" });
      toast.success("Plan actualizado correctamente");
      refetchSub();
      refetchUsage();
    } catch (e: any) {
      toast.error(friendlyError(e));
    }
  };

  const handleCancel = async () => {
    const confirmed = window.confirm(
      "Tu suscripción seguirá activa hasta el final del periodo de facturación. ¿Deseas cancelar?",
    );
    if (!confirmed) return;

    try {
      await cancelSubscription();
      toast.success("Suscripción marcada para cancelar al final del periodo");
      refetchSub();
    } catch (e: any) {
      toast.error(friendlyError(e));
    }
  };

  const handleReactivate = async () => {
    try {
      await reactivateSubscription();
      toast.success("Suscripción reactivada");
      refetchSub();
    } catch (e: any) {
      toast.error(friendlyError(e));
    }
  };

  const handleManageBilling = async () => {
    try {
      const { data } = await manageBilling();
      if (data?.manageBilling.url) {
        window.location.href = data.manageBilling.url;
      }
    } catch (e: any) {
      toast.error(friendlyError(e));
    }
  };

  const planCardsRef = React.useRef<HTMLDivElement>(null);

  if (plansLoading || subLoading || usageLoading) {
    return <LoaderDots />;
  }

  const plans = plansData?.getSubscriptionPlans || [];
  const subscription = subData?.getMySubscription;
  const usage = usageData?.getPlanUsage;

  const statusLabels: Record<string, { text: string; color: string }> = {
    active: { text: "Activa", color: "#48BB78" },
    past_due: { text: "Pago pendiente", color: "#FF6028" },
    cancelled: { text: "Cancelada", color: "#999" },
    pending: { text: "Pendiente", color: "#FFBB28" },
  };

  const planTypeLabels: Record<string, string> = {
    basic: "Basico",
    premium: "Premium",
    pro: "Pro",
  };

  const scrollToPlans = () => {
    planCardsRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="container py-4">
      <HeaderTitle title="Suscripción" />

      {/* Trial Banner */}
      {usage?.isOnTrial && !subscription && (
        <div
          className="card mb-4"
          style={{ borderColor: "#FFBB28", borderWidth: "2px" }}
        >
          <div className="card-body">
            <div className="d-flex align-items-center mb-2">
              <i
                className="fas fa-clock mr-2"
                style={{ color: "#FFBB28", fontSize: "1.5rem" }}
              />
              <h5 className="card-title mb-0">Periodo de Prueba Activo</h5>
            </div>
            <p className="mb-2">
              Te quedan <strong>{usage.trialDaysRemaining} dias</strong> de tu
              periodo de prueba. Durante el trial tienes acceso a funciones
              Premium.
            </p>
            <button
              className="btn btn-sm"
              style={{ backgroundColor: "#FFBB28", color: "#292929" }}
              onClick={scrollToPlans}
            >
              Ver Planes
            </button>
          </div>
        </div>
      )}

      {/* Current Plan Card */}
      {subscription && (
        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h5 className="card-title mb-1">Tu Plan Actual</h5>
                <h3 className="mb-2">
                  {planTypeLabels[subscription.plan_type] ||
                    subscription.plan_type}
                </h3>
                <span
                  className="badge"
                  style={{
                    backgroundColor:
                      statusLabels[subscription.status]?.color || "#999",
                    color: "#fff",
                  }}
                >
                  {statusLabels[subscription.status]?.text ||
                    subscription.status}
                </span>
                {subscription.cancel_at_period_end && (
                  <span className="badge bg-warning text-dark ml-2">
                    Se cancela al final del periodo
                  </span>
                )}
              </div>
              <div className="text-right">
                {subscription.period_end && (
                  <p className="text-muted mb-1">
                    Proxima facturacion:{" "}
                    {new Date(subscription.period_end).toLocaleDateString(
                      "es-MX",
                    )}
                  </p>
                )}
                {subscription.amount && (
                  <p className="mb-0">
                    <strong>
                      ${subscription.amount.toLocaleString("es-MX")} /año (incluye IVA)
                    </strong>
                  </p>
                )}
              </div>
            </div>
            <hr />
            <div className="d-flex gap-2">
              {subscription.status === "active" &&
                !subscription.cancel_at_period_end && (
                  <button
                    className="btn btn-outline-danger btn-sm mr-2"
                    onClick={handleCancel}
                    disabled={cancelLoading}
                  >
                    Cancelar Suscripción
                  </button>
                )}
              {subscription.cancel_at_period_end && (
                <button
                  className="btn btn-outline-success btn-sm mr-2"
                  onClick={handleReactivate}
                  disabled={reactivateLoading}
                >
                  Reactivar Suscripción
                </button>
              )}
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={handleManageBilling}
                disabled={billingLoading}
              >
                <i className="fas fa-credit-card mr-1" />
                Administrar Metodos de Pago
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Usage Card */}
      {usage && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title mb-3">
              Uso del Plan
              {usage.isOnTrial && !subscription && (
                <span
                  className="badge ml-2"
                  style={{ backgroundColor: "#FFBB28", color: "#292929" }}
                >
                  Prueba (Premium)
                </span>
              )}
            </h5>
            <div className="row">
              <div className="col-md-4 mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <small>Usuarios</small>
                  <small>
                    {usage.currentUsers} / {usage.maxUsers}
                  </small>
                </div>
                <div className="progress" style={{ height: "8px" }}>
                  <div
                    className="progress-bar"
                    style={{
                      width: `${Math.min((usage.currentUsers / usage.maxUsers) * 100, 100)}%`,
                      backgroundColor:
                        usage.currentUsers >= usage.maxUsers
                          ? "#FF6028"
                          : "#48BB78",
                    }}
                  />
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <small>Sucursales</small>
                  <small>
                    {usage.currentBranches} / {usage.maxBranches}
                  </small>
                </div>
                <div className="progress" style={{ height: "8px" }}>
                  <div
                    className="progress-bar"
                    style={{
                      width: `${Math.min((usage.currentBranches / usage.maxBranches) * 100, 100)}%`,
                      backgroundColor:
                        usage.currentBranches >= usage.maxBranches
                          ? "#FF6028"
                          : "#48BB78",
                    }}
                  />
                </div>
              </div>
              <div className="col-md-4 mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <small>Ordenes este mes</small>
                  <small>
                    {usage.currentOrders} / {usage.maxOrders}
                  </small>
                </div>
                <div className="progress" style={{ height: "8px" }}>
                  <div
                    className="progress-bar"
                    style={{
                      width: `${Math.min((usage.currentOrders / usage.maxOrders) * 100, 100)}%`,
                      backgroundColor:
                        usage.currentOrders >= usage.maxOrders
                          ? "#FF6028"
                          : "#48BB78",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Plan Cards */}
      <div ref={planCardsRef} />
      <h4 className="mb-3">Planes Disponibles</h4>
      <PlanCards
        plans={plans}
        currentPlanType={subscription?.plan_type || null}
        currentBillingInterval={subscription?.interval || null}
        onSubscribe={handleSubscribe}
        onUpgrade={handleUpgrade}
        subscribeLoading={checkoutLoading}
        upgradeLoading={upgradeLoading}
      />

      {/* Upgrade Confirmation Modal */}
      {upgradeModal.open && (() => {
        const currentPlan = subscription?.plan_type || "";
        const newPlan = upgradeModal.planType;
        const currentLabel = planTypeLabelsModal[currentPlan] || currentPlan;
        const newLabel = planTypeLabelsModal[newPlan] || newPlan;
        const newColor = planColorsModal[newPlan] || "#FFBB28";
        const newFeatures = planFeaturesModal[newPlan] || [];
        const newPlanData = plans.find((p) => p.key === newPlan);
        const currentPlanData = plans.find((p) => p.key === currentPlan);
        const isMonthly = upgradeModal.billingInterval === "monthly";
        const periodLabel = isMonthly ? "/mes" : "/año";

        return (
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            role="dialog"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            onClick={() => setUpgradeModal({ open: false, planType: "", billingInterval: "yearly" })}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              role="document"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div
                  className="modal-header"
                  style={{ borderBottom: `3px solid ${newColor}` }}
                >
                  <h5 className="modal-title">
                    <i className="fas fa-arrow-circle-up mr-2" style={{ color: newColor }} />
                    Mejorar tu suscripción
                  </h5>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setUpgradeModal({ open: false, planType: "", billingInterval: "yearly" })}
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  {/* Plan change summary */}
                  <div className="d-flex align-items-center justify-content-center mb-4">
                    <div className="text-center">
                      <span
                        className="badge badge-lg px-3 py-2"
                        style={{
                          backgroundColor: planColorsModal[currentPlan] || "#999",
                          color: "#fff",
                          fontSize: "0.95rem",
                        }}
                      >
                        {currentLabel}
                      </span>
                      {currentPlanData && (
                        <div className="text-muted mt-1" style={{ fontSize: "0.85rem" }}>
                          ${(isMonthly ? currentPlanData.priceMonthly : currentPlanData.priceYearly).toLocaleString("es-MX")}{periodLabel} (incluye IVA)
                        </div>
                      )}
                    </div>
                    <i
                      className="fas fa-arrow-right mx-3"
                      style={{ fontSize: "1.3rem", color: "#999" }}
                    />
                    <div className="text-center">
                      <span
                        className="badge badge-lg px-3 py-2"
                        style={{
                          backgroundColor: newColor,
                          color: "#fff",
                          fontSize: "0.95rem",
                        }}
                      >
                        {newLabel}
                      </span>
                      {newPlanData && (
                        <div className="mt-1" style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                          ${(isMonthly ? newPlanData.priceMonthly : newPlanData.priceYearly).toLocaleString("es-MX")}{periodLabel} (incluye IVA)
                        </div>
                      )}
                    </div>
                  </div>

                  {/* New plan features */}
                  <div
                    className="p-3 mb-3"
                    style={{
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      border: `1px solid ${newColor}20`,
                    }}
                  >
                    <p className="mb-2" style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                      Con el plan {newLabel} tendras:
                    </p>
                    <ul className="list-unstyled mb-0">
                      {newFeatures.map((feature, i) => (
                        <li key={i} className="mb-1">
                          <i
                            className="fas fa-check-circle mr-2"
                            style={{ color: newColor }}
                          />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Proration explanation */}
                  <div
                    className="p-3"
                    style={{
                      backgroundColor: "#FFF9E6",
                      borderRadius: "8px",
                      border: "1px solid #FFBB2840",
                    }}
                  >
                    <p className="mb-0" style={{ fontSize: "0.85rem" }}>
                      <i className="fas fa-info-circle mr-2" style={{ color: "#FFBB28" }} />
                      Se calculara un prorrateo automatico: el tiempo restante de tu plan{" "}
                      <strong>{currentLabel}</strong> se abonara al costo del plan{" "}
                      <strong>{newLabel}</strong>. El cobro de la diferencia se realizara de
                      inmediato a tu metodo de pago registrado.
                    </p>
                  </div>
                </div>
                <div className="modal-footer" style={{ borderTop: "none" }}>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setUpgradeModal({ open: false, planType: "", billingInterval: "yearly" })}
                    disabled={upgradeLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn"
                    style={{
                      backgroundColor: newColor,
                      color: "#fff",
                      fontWeight: 600,
                    }}
                    onClick={confirmUpgrade}
                    disabled={upgradeLoading}
                  >
                    {upgradeLoading ? (
                      <span
                        className="spinner-border spinner-border-sm mr-2"
                        role="status"
                      />
                    ) : (
                      <i className="fas fa-rocket mr-2" />
                    )}
                    Sí, mejorar mi suscripción
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default SubscriptionPage;
